import $           from 'jquery';
import trialPeriod from './trialPeriod';

// server `trial_code_error` to human-friendly messages
const promo_errors = {
  1: 'We don\'t recognize that code. Try again?',
  2: 'Your address isn\'t eligible for that code.',
  3: 'That code isn\'t active yet. Try again soon!',
  4: 'Aw, shoot. That code is expired.',
  5: 'Aw, shoot. That code is no longer valid.'
};

/**
 * @function get_trial_offer
 *
 * given a trialcode and plan, get the corresponding "offer"
 * which will be the entire trialcode for v1 trialcodes, or a matching price
 * tier for v2
 *
 * @param  {object?} trialcode – trialcode object (or null/undefined)
 * @param  {object?} plan      – plan object (or null/undefined)
 * @return {object|null}
 */

export function get_trial_offer({ trialcode, plan }) {
  if ( !trialcode ) {
    return null;
  }

  if ( !trialcode.tiers ) {
    return trialcode;
  }

  if ( !plan ) {
    return null;
  }

  const clone  = trialcode.tiers.slice();
  const sorted = clone.sort( ( a, b ) => b.min_price - a.min_price );

  return sorted.find( ({ min_price, max_price }) => {
    return plan.amount >= min_price && (
      max_price === null || ( plan.amount <= max_price )
    );
  }) || null;
}

/**
 * @function get_trial_days
 *
 * given a trialcode object and a plan object, get the correct
 * number of trial days
 *
 * @param  {object?} trialcode – trialcode object (or null/undefined)
 * @param  {object?} plan      – plan object (or null/undefined)
 * @return {number}
 */

export function get_trial_days({ trialcode, plan }) {
  if ( !plan ) {
    return 0;
  }

  const base  = plan.base_trial_days | 0;
  const offer = get_trial_offer({ trialcode, plan });

  if ( !offer ) {
    return base;
  }

  if ( should_force_promo({ trialcode, plan }) ) {
    return offer.trial_period_days | 0;
  }

  return Math.max( base, offer.trial_period_days | 0 );
}

/**
 * @function should_force_promo
 *
 * given a trialcode object and a plan object, determine whether the
 * trialcode should always be applied, even when it offers fewer free days than
 * the user's existing offer
 *
 * @param  {object?} trialcode – trialcode object (or null/undefined)
 * @param  {object?} plan      – plan object (or null/undefined)
 * @return {boolean}
 */

export function should_force_promo({ trialcode, plan }) {
  const offer = get_trial_offer({ trialcode, plan });
  return offer && ( offer.message || offer.gift_card_id );
}

/**
 * @function check
 *
 * Given a trialcode name, plan object, place_id, and duration of the customer's
 * existing trial offer, return an object containing the applied
 * trialcode (if any), as well as any errors or user-facing success messages
 *
 * @param  {string} name           – promo code name
 * @param  {object} plan           – user's selected plan
 * @param  {string} place_id       – user's place_id
 * @param  {number} previous_trial – user's current trial offer (in days)
 * @return {object}                  { trialcode?, message?, error? }
 */

export async function check({ name, plan, place_id, previous_trial }) {
  try {
    const path      = `/trialcodes/${ name.trim().toLowerCase() }`;
    const query     = place_id ? `?place_id=${ place_id }` : '';
    const url       = `${ window.cloudURL }${ path }${ query }`;
    const trialcode = await $.get( url );
    const is_lte_30 = plan.amount <= 3000;
    const is_v2     = trialcode.tiers;
    const offer     = get_trial_offer({ trialcode, plan });
    const days      = ( offer || {} ).trial_period_days | 0;
    const unit      = days % 30 === 0 ? 'month' : 'day';
    const value     = unit === 'month' ? days / 30 : days;
    const plural    = value > 1 ? 's' : '';
    const duration  = `${ value } ${ unit }${ plural }`;
    const message   = `Promo code applied. You just got ${ duration } free!`;

    // promos w/ custom messages of gift cards always get applied
    if ( offer && should_force_promo({ trialcode, plan }) ) {
      return {
        trialcode,
        message: offer.message || message,
        error_code: null,
        error: null
      };
    }

    // can't apply high-value promos to less expensive plans
    const is_low_value = !is_v2 && is_lte_30 && days > 37;

    // low-value legacy plan or no matching tier
    if ( is_low_value || !offer ) {
      return {
        trialcode: null,
        message: null,
        error_code: 'plan_not_eligible',
        error: 'The plan you selected is not eligible for this promotion.'
      };
    }

    // worse than existing promo
    if ( offer.trial_period_days < previous_trial ) {
      return {
        trialcode: null,
        message: null,
        error_code: 'promo_less_favorable',
        error: 'The existing promo was better than that code, so we skipped it.'
      };
    }

    return { trialcode, message, error: null };
  } catch ( ex ) {
    const data = ex.responseJSON;
    const code = data && data.trial_code_error;

    let message = 'An unknown error occurred. Please try again';

    // evidence that we've got a custom error message
    if ( code && data.message ) {
      message = promo_errors[ code ] || data.message;
    }

    return {
      trialcode: null,
      message: null,
      error_code: trialPeriod.getErrorTag( code ),
      error: message
    };
  }
}
